<template>
  <ValidationObserver ref="booking-form-observer" v-slot="{ handleSubmit }">
    <b-form @submit.prevent="handleSubmit(submit)">
      <hr />
      <b-row>
        <b-col md="6" lg="3" xl="4">
          <h4>Customer Details</h4>
        </b-col>
        <b-col md="6" xl="4">
          <ValidationProvider
            name="name"
            rules="required|min:1"
            v-slot="{ dirty, validated, valid, errors }"
          >
            <b-form-group label="Name *">
              <b-form-input
                type="text"
                name="name"
                v-model="model.customer.name"
                :state="dirty || validated ? valid : null"
                :disabled="model.bookingStatus === 'CANCELLED'"
                placeholder="Enter name"
              />
              <b-form-invalid-feedback>
                {{ errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </ValidationProvider>
          <ValidationProvider
            name="phone number"
            rules="required"
            v-slot="{ dirty, validated, valid, errors }"
          >
            <b-form-group label="Phone Number *">
              <b-form-input
                type="tel"
                v-model="model.customer.phoneNumber"
                :state="dirty || validated ? valid : null"
                :disabled="model.bookingStatus === 'CANCELLED'"
                placeholder="Enter phone number"
              />
              <b-form-invalid-feedback>
                {{ errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </ValidationProvider>
          <ValidationProvider
            name="email"
            rules="email"
            v-slot="{ dirty, validated, valid, errors }"
          >
            <b-form-group
              label="Email Address"
              description="The customer will receive a booking confirmation as well as booking reminders if an email address is provided."
            >
              <b-form-input
                type="email"
                v-model="model.customer.emailAddress"
                :state="
                  !model.customer.emailAddress
                    ? null
                    : dirty || validated
                    ? valid
                    : null
                "
                :disabled="model.bookingStatus === 'CANCELLED'"
                placeholder="Enter email address"
              />
              <b-form-invalid-feedback>
                {{ errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </ValidationProvider>
        </b-col>
      </b-row>

      <hr />

      <b-row>
        <b-col md="6" lg="3" xl="4">
          <h4>Service</h4>
        </b-col>
        <b-col md="6" xl="4">
          <ValidationProvider
            name="barber"
            rules="required"
            v-slot="{ dirty, validated, valid, errors }"
          >
            <b-form-group label="Barber *">
              <b-form-select
                v-model="model.resource"
                :options="selectableResources"
                :state="dirty || validated ? valid : null"
                :disabled="
                  (editMode && model.bookingStatus === 'CONFIRMED') ||
                    model.bookingStatus === 'CANCELLED'
                "
                @change="onBookingResourceSelected"
              />
              <b-form-invalid-feedback>
                {{ errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </ValidationProvider>
          <ValidationProvider
            name="service"
            rules="required"
            v-slot="{ dirty, validated, valid, errors }"
          >
            <b-form-group label="Service *">
              <b-form-select
                v-model="model.service"
                :options="selectableServices"
                :state="dirty || validated ? valid : null"
                :disabled="
                  !model.resource ||
                    (editMode && model.bookingStatus === 'CONFIRMED') ||
                    model.bookingStatus === 'CANCELLED'
                "
                required
                @change="onBookingServiceSelected"
              />
              <b-form-invalid-feedback>
                {{ errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </ValidationProvider>
          <ValidationProvider
            name="package"
            rules="required"
            v-slot="{ dirty, validated, valid, errors }"
          >
            <b-form-group label="Package *">
              <b-form-select
                v-model="model.package"
                :options="selectablePackages"
                :state="dirty || validated ? valid : null"
                :disabled="
                  !model.service ||
                    (editMode && model.bookingStatus === 'CONFIRMED') ||
                    model.bookingStatus === 'CANCELLED'
                "
                required
                @change="onBookingPackageSelected"
              />
              <b-form-invalid-feedback>
                {{ errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </ValidationProvider>
        </b-col>
      </b-row>

      <hr />

      <b-row>
        <b-col md="6" lg="3" xl="4">
          <h4>Date & Time</h4>
        </b-col>
        <b-col md="6" xl="4">
          <b-form-group label="Date *">
            <vue-ctk-date-time-picker
              label="Select date"
              :value="model.startDate"
              format="YYYY-MM-DD"
              formatted="ll"
              :only-date="true"
              :right="true"
              :no-label="true"
              :disabled="
                (editMode && model.bookingStatus === 'CONFIRMED') ||
                  model.bookingStatus === 'CANCELLED'
              "
              noClearButton
              required
              @input="setDate"
            />
          </b-form-group>
          <b-row>
            <b-col xs="12" md="6">
              <b-form-group label="Start Time *">
                <vue-ctk-date-time-picker
                  label="Select start time"
                  v-model="model.startTime"
                  format="HH:mm"
                  formatted="hh:mm a"
                  :only-time="true"
                  :right="true"
                  :minute-interval="5"
                  :no-label="true"
                  :disabled="
                    (editMode && model.bookingStatus === 'CONFIRMED') ||
                      model.bookingStatus === 'CANCELLED'
                  "
                  no-clear-button
                  required
                  @input="calculateBookingEndTime"
                />
              </b-form-group>
            </b-col>
            <b-col xs="12" md="6">
              <b-form-group label="End Time *">
                <vue-ctk-date-time-picker
                  label="Select end time"
                  v-model="model.endTime"
                  format="HH:mm"
                  formatted="hh:mm a"
                  :only-time="true"
                  :right="true"
                  :minute-interval="5"
                  :no-label="true"
                  :disabled-hours="endTimeDisabledHours"
                  :disabled="
                    (editMode && model.bookingStatus === 'CONFIRMED') ||
                      model.bookingStatus === 'CANCELLED'
                  "
                  no-clear-button
                  required
                />
              </b-form-group>
            </b-col>
          </b-row>
        </b-col>
      </b-row>

      <hr />

      <b-row>
        <b-col md="6" lg="3" xl="4">
          <h4>Total</h4>
        </b-col>
        <b-col md="6" xl="4">
          <b-form-group
            description="The total is determined by the chosen Service and Package."
          >
            <b-input-group prepend="€">
              <b-form-input
                name="total"
                v-model="model.price"
                type="number"
                disabled
              />
            </b-input-group>
          </b-form-group>
        </b-col>
      </b-row>

      <hr />

      <b-row>
        <b-col md="6" lg="3" xl="4">
          <h4>Notes</h4>
        </b-col>
        <b-col md="6" xl="4">
          <b-form-group>
            <b-form-textarea v-model="model.notes" rows="3" max-rows="3" />
          </b-form-group>
        </b-col>
      </b-row>

      <hr />

      <b-row>
        <b-col offset-lg="3" offset-xl="4">
          <b-button variant="primary" type="submit">
            <b-spinner
              v-if="isSaving"
              variant="light"
              style="height: 20px; width: 20px;"
              label="Submitting booking"
            ></b-spinner>
            <div v-else>
              Save
            </div>
          </b-button>
          <b-button
            variant="link"
            type="button"
            role="navigation"
            @click.prevent="onCancel"
          >
            Cancel
          </b-button>
        </b-col>
      </b-row>
    </b-form>
  </ValidationObserver>
</template>
<script>
import moment from 'moment';
import _ from 'lodash';
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import { mapActions, mapGetters } from 'vuex';

import { DEFAULT_BOOKING, bookingStatusTypes } from '@/store/modules/bookings';
import { goBack } from '@/mixins';

export default {
  mixins: [goBack],
  components: {
    ValidationObserver,
    ValidationProvider
  },
  props: {
    id: String,
    booking: Object,
    editMode: Boolean
  },
  data() {
    return {
      model: _.cloneDeep(DEFAULT_BOOKING),
      isSaving: false
    };
  },
  methods: {
    ...mapActions({
      createBooking: 'bookings/createBooking',
      updateBooking: 'bookings/updateBooking',
      cancelBooking: 'bookings/cancelBooking',
      fetchResources: 'resources/fetchResources',
      fetchServices: 'services/fetchServices',
      fetchPackages: 'packages/fetchPackages'
    }),
    setup() {
      this.model = {
        ...this.model,
        ...this.booking
      };
    },
    onBookingResourceSelected(resource) {
      if (
        !resource ||
        !_.get(this.booking, `service.resourcesEnabled[${resource._id}]`)
      ) {
        this.model.service = null;
        this.model.package = null;
        this.model.price = null;
      }
    },
    onBookingServiceSelected(service) {
      if (
        !service ||
        (this.model.package && !service.packagesEnabled[this.model.package._id])
      ) {
        this.model.package = null;
        this.model.price = null;
      }

      this.calculateBookingEndTime();
    },
    onBookingPackageSelected(_package) {
      this.model.price = !_package
        ? 0
        : _.get(this.model, `service.pricesForPackages[${_package._id}]`, 0);
    },
    setDate(date) {
      this.model.startDate = date;
      this.model.endDate = date;
    },
    calculateBookingEndTime() {
      const model = this.model;

      const endTime = moment(this.model.startTime, 'HH:mm')
        .add(_.get(model, 'service.baseDuration', 15), 'minutes')
        .format('HH:mm');

      model.endTime = endTime;
    },
    async submit() {
      this.showSavingIndicator();

      const booking = this.sanitize(this.model);

      try {
        if (this.editMode) {
          await this.updateBooking({ booking });
        } else {
          booking.bookingStatus = bookingStatusTypes.CONFIRMED;
          await this.createBooking({ booking });
        }

        this.$emit('saved');
      } catch (e) {
        if (e.message === 'Booking slot not available.') {
          this.onResourceNotAvailable(booking);
        } else {
          this.$notify({
            type: 'error',
            title: 'Error',
            text: e.message
          });
        }
      } finally {
        this.hideSavingIndicator();
      }
    },
    showSavingIndicator() {
      this.isSaving = true;
    },
    hideSavingIndicator() {
      this.isSaving = false;
    },
    sanitize(booking) {
      let { price, startDate, startTime, endDate, endTime } = booking;
      const [startHours, startMinutes] = startTime.split(':');
      const [endHours, endMinutes] = endTime.split(':');

      if (endDate < startDate) endDate = startDate;

      return {
        ...booking,
        price: parseFloat(price),
        start: moment(startDate, 'YYYY-MM-DD')
          .set({ hours: startHours, minutes: startMinutes })
          .format(),
        endDate,
        end: moment(endDate, 'YYYY-MM-DD')
          .set({ hours: endHours, minutes: endMinutes })
          .format()
      };
    },
    onResourceNotAvailable(booking) {
      this.$bvModal
        .msgBoxConfirm(
          `Please note that ${booking.resource.name} is not available for this time.`,
          {
            title: 'Barber Not Available',
            size: 'sm',
            buttonSize: 'sm',
            okVariant: 'danger',
            okTitle: 'Save Anyway',
            cancelTitle: 'Cancel',
            footerClass: 'p-2',
            hideHeaderClose: false,
            centered: true
          }
        )
        .then(value => {
          if (value) {
            this.forceSubmit(booking);
          }
        })
        .catch(e => {
          this.$notify({
            type: 'error',
            title: 'Error',
            text: e.message
          });
        });
    },
    async forceSubmit(booking) {
      try {
        if (this.editMode) {
          await this.updateBooking({ booking, override: true });
        } else {
          await this.createBooking({ booking, override: true });
        }

        this.$emit('saved');
      } catch (e) {
        this.$notify({
          type: 'error',
          title: 'Error',
          text: e.message
        });
      }
    },
    onCancel() {
      this.$emit('cancel');
    }
  },
  computed: {
    ...mapGetters({
      bookings: 'bookings/bookings',
      resourcesList: 'resources/resourcesList',
      servicesList: 'services/servicesList',
      packagesList: 'packages/packagesList',
      confirmedBookings: 'bookings/confirmedBookings',
      storeId: 'stores/storeId'
    }),
    selectableResources() {
      return [
        {
          value: null,
          text: 'Select a Barber',
          disabled: true
        },
        ..._.map(this.resourcesList, resource => ({
          value: resource,
          text: resource.name
        }))
      ];
    },
    selectableServices() {
      const resourceId = _.get(this.model, 'resource._id', null);

      const servicesEnabledForResource = !resourceId
        ? []
        : _.reduce(
            this.servicesList,
            (services, service) => {
              if (service.resourcesEnabled[resourceId]) {
                services.push({
                  value: service,
                  text: service.name
                });
              }

              return services;
            },
            []
          );

      return [
        {
          value: null,
          text: 'Select a Service',
          disabled: true
        },
        ...servicesEnabledForResource
      ];
    },
    selectablePackages() {
      const service = this.model.service;

      const packagesEnabledForService = !service
        ? []
        : _.reduce(
            this.packagesList,
            (packages, _package) => {
              if (service.packagesEnabled[_package._id]) {
                packages.push({
                  value: _package,
                  text: `${_package.name} - €${
                    service.pricesForPackages[_package._id]
                  }`
                });
              }

              return packages;
            },
            []
          );

      return [
        {
          value: null,
          text: 'Select a Package',
          disabled: true
        },
        ...packagesEnabledForService
      ];
    },
    endTimeDisabledHours() {
      const { startTime } = this.model;

      if (!startTime || startTime === '') {
        return [];
      }

      const startHour = moment(startTime, 'HH:mm').get('hours');
      const disabledHours = _.range(startHour);
      return _.map(disabledHours, hour => _.padStart(`${hour}`, 2, '0'));
    }
  },
  created() {
    this.fetchResources();
    this.fetchServices();
    this.fetchPackages();
  },
  watch: {
    booking: {
      immediate: true,
      handler() {
        this.setup();
      }
    }
  }
};
</script>
<style lang="scss" scoped>
.label-paid {
  font-size: 1.2rem;
  font-weight: bold;
}
</style>
